import React, {FC, useState, useEffect, useRef} from 'react'
import clsx from 'clsx'
import Swal from 'sweetalert2'
import {KTIcon} from '../../../../_payairo/helpers'
import {
  setup2FA,
  verify2FA,
  select2FAMethod,
  requestEmailOTP,
  verifyEmailOTP,
  TwoFactorSetupResponseData,
} from '../services/twoFactorService'

interface TwoFactorSetupProps {
  tempToken: string
  userEmail?: string
  is2faEnabled?: boolean
  initialMethod?: 'EMAIL' | 'AUTHENTICATOR' | null
  onSuccess?: (authData?: any) => void
  onCancel?: () => void
}

export const TwoFactorSetup: FC<TwoFactorSetupProps> = ({
  tempToken,
  userEmail = '',
  is2faEnabled = false,
  initialMethod = null,
  onSuccess,
  onCancel,
}) => {
  const cleanEmail = (userEmail || '').toLowerCase().trim()
  const hasAccountStoredSetup = cleanEmail ? localStorage.getItem(`2fa_enabled_${cleanEmail}`) === 'true' : false

  // NEW STATE: Method selection
  const [selectedMethod, setSelectedMethod] = useState<'EMAIL' | 'AUTHENTICATOR' | null>(initialMethod)
  const [methodLoading, setMethodLoading] = useState<boolean>(false)

  const [loading, setLoading] = useState<boolean>(false)
  const [verifying, setVerifying] = useState<boolean>(false)
  const [resetting, setResetting] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)
  const [setupData, setSetupData] = useState<TwoFactorSetupResponseData | null>(null)
  
  // 6-digit OTP state
  const [otp, setOtp] = useState<string[]>(['', '', '', '', '', ''])
  const [copied, setCopied] = useState<boolean>(false)
  
  const inputRefs = useRef<(HTMLInputElement | null)[]>([])

  const [isAlreadySetup, setIsAlreadySetup] = useState<boolean>(is2faEnabled)

  const init2FASetup = async () => {
    if (!tempToken) {
      setError('Invalid session token. Please try logging in again.')
      return
    }

    setLoading(true)
    try {
      const response = await setup2FA(tempToken)
      const resData = response.data || (response as any)
      setSetupData(resData)
      setIsAlreadySetup(false)
    } catch (err: any) {
      console.error('2FA Setup Error:', err)
      const errMessage = err?.response?.data?.message || err?.message || 'Failed to load QR code.'
      if (errMessage.toLowerCase().includes('already enabled')) {
        setIsAlreadySetup(true)
      } else {
        setError(errMessage)
      }
    } finally {
      setLoading(false)
    }
  }

  const handleReset2FA = async () => {
    if (!tempToken) {
      setError('Invalid session token. Please try logging in again.')
      return
    }

    setResetting(true)
    setError(null)
    try {
      const response = await setup2FA(tempToken)
      const resData = response.data || (response as any)
      setSetupData(resData)
      setIsAlreadySetup(false)
    } catch (err: any) {
      console.error('2FA Reset Error:', err)
      const errMessage = err?.response?.data?.message || err?.message || 'Failed to reset 2FA.'
      if (errMessage.toLowerCase().includes('already enabled')) {
        setIsAlreadySetup(true)
      } else {
        setError(errMessage)
      }
    } finally {
      setResetting(false)
    }
  }

  // Handle Initial Method from Backend
  useEffect(() => {
    let isMounted = true

    const handleInitialMethod = async () => {
      if (!initialMethod) return
      
      setLoading(true)
      try {
        if (initialMethod === 'EMAIL') {
          await requestEmailOTP(tempToken)
          if (isMounted) setIsAlreadySetup(true)
        } else if (initialMethod === 'AUTHENTICATOR') {
          if (!is2faEnabled) {
            await init2FASetup()
          } else {
            if (isMounted) setIsAlreadySetup(true)
          }
        }
      } catch (err: any) {
        console.error('Initial Method Setup Error:', err)
        if (isMounted) {
          setError(err?.response?.data?.message || err?.message || 'Failed to initialize 2FA method.')
        }
      } finally {
        if (isMounted) setLoading(false)
      }
    }

    handleInitialMethod()

    return () => {
      isMounted = false
    }
  }, [initialMethod, tempToken, is2faEnabled])

  // Handle Method Selection
  const handleMethodSelect = async (method: 'EMAIL' | 'AUTHENTICATOR') => {
    setMethodLoading(true)
    setError(null)
    try {
      // 1. Send method selection to backend
      const response = await select2FAMethod(tempToken, method)
      const resStatus = response?.data?.status || response?.status || ''
      const isSetupReq = resStatus.toUpperCase().includes('SETUP_REQUIRED')
      
      setSelectedMethod(method)
      
      // 2. If AUTHENTICATOR and setup is required, fetch setup data (QR code)
      if (method === 'AUTHENTICATOR') {
        if (isSetupReq || !is2faEnabled) {
          await init2FASetup()
        } else {
          setIsAlreadySetup(true)
        }
      } else if (method === 'EMAIL') {
        // Request Email OTP, then bypass setup and go straight to OTP
        await requestEmailOTP(tempToken)
        setIsAlreadySetup(true)
      }
    } catch (err: any) {
      console.error('Method Selection Error:', err)
      const errMessage = err?.response?.data?.message || err?.message || 'Failed to select 2FA method.'
      setError(errMessage)
    } finally {
      setMethodLoading(false)
    }
  }


  // Extract Secret Key and QR Code
  const rawSetupData = setupData as any
  const secretKey =
    rawSetupData?.secret ||
    rawSetupData?.data?.secret ||
    rawSetupData?.secret_key ||
    rawSetupData?.data?.secret_key ||
    rawSetupData?.otp_secret ||
    rawSetupData?.data?.otp_secret ||
    rawSetupData?.manual_entry_key ||
    ''

  const rawQrCode =
    rawSetupData?.qr_code ||
    rawSetupData?.data?.qr_code ||
    rawSetupData?.qr_code_url ||
    rawSetupData?.data?.qr_code_url ||
    ''

  const getFormattedQrCodeUrl = (qr: string): string => {
    if (!qr) return ''
    if (qr.startsWith('http') || qr.startsWith('data:image')) return qr
    if (qr.startsWith('<svg')) {
      return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(qr)}`
    }
    return `data:image/png;base64,${qr}`
  }

  const qrCodeUrl = getFormattedQrCodeUrl(rawQrCode)

  const handleCopySecretKey = () => {
    if (!secretKey) return
    navigator.clipboard.writeText(secretKey)
    setCopied(true)
    setTimeout(() => setCopied(false), 2500)
  }

  const handleOtpChange = (index: number, value: string) => {
    const cleanValue = value.replace(/[^0-9]/g, '')
    if (!cleanValue && value !== '') return

    const newOtp = [...otp]
    
    if (cleanValue.length > 1) {
      const pastedDigits = cleanValue.slice(0, 6).split('')
      for (let i = 0; i < 6; i++) {
        newOtp[i] = pastedDigits[i] || ''
      }
      setOtp(newOtp)
      const lastIndex = Math.min(pastedDigits.length - 1, 5)
      inputRefs.current[lastIndex]?.focus()
      return
    }

    newOtp[index] = cleanValue
    setOtp(newOtp)

    if (cleanValue && index < 5) {
      inputRefs.current[index + 1]?.focus()
    }
  }

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus()
    }
  }

  const handleVerifySubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    const fullOtp = otp.join('')

    if (fullOtp.length < 6) {
      Swal.fire({
        icon: 'warning',
        title: 'Incomplete OTP',
        text: 'Please enter all 6 digits.',
        confirmButtonColor: '#009ef7',
      })
      return
    }

    setVerifying(true)
    try {
      let verifyRes;
      if (selectedMethod === 'EMAIL') {
        verifyRes = await verifyEmailOTP(tempToken, fullOtp)
      } else {
        verifyRes = await verify2FA(tempToken, fullOtp)
      }
      
      if (verifyRes.ok || (verifyRes as any).status === true) {
        if (cleanEmail) {
          localStorage.setItem(`2fa_enabled_${cleanEmail}`, 'true')
        }
        localStorage.setItem('2fa_last_verified', 'true')
        Swal.fire({
          icon: 'success',
          title: '2FA Verification Successful',
          text: 'Two-Factor Authentication verified successfully!',
          timer: 1800,
          showConfirmButton: false,
        })
        if (onSuccess) onSuccess(verifyRes.data || verifyRes)
      } else {
        throw new Error(verifyRes.message || 'Invalid 2FA code. Please try again.')
      }
    } catch (err: any) {
      console.error('2FA Verification Error:', err)
      const errorMsg =
        err?.response?.data?.message ||
        err?.response?.data?.error?.message ||
        err?.message ||
        'Verification failed. Please check the code and try again.'

      Swal.fire({
        icon: 'error',
        title: 'Verification Failed',
        text: errorMsg,
        confirmButtonColor: '#f1416c',
      })
    } finally {
      setVerifying(false)
    }
  }

  // SCREEN 1: Method Selection
  if (!selectedMethod) {
    return (
      <div className='w-100 p-2'>
        <div className='text-center mb-8'>
          <div className='d-inline-flex align-items-center justify-content-center w-60px h-60px rounded-circle bg-light-primary mb-4'>
            <KTIcon iconName='shield-tick' className='fs-2x text-primary' />
          </div>
          <h2 className='text-dark fw-bolder mb-2'>Choose 2FA Method</h2>
          <div className='text-gray-500 fw-semibold fs-6'>
            Select how you want to receive your verification code
          </div>
        </div>

        {error && (
          <div className='alert alert-danger mb-8'>
            <div className='alert-text font-weight-bold'>{error}</div>
          </div>
        )}

        <div className='d-flex flex-column gap-5 mb-8'>
          <button
            type='button'
            className='btn btn-outline btn-outline-dashed btn-outline-primary btn-active-light-primary p-7 d-flex align-items-center'
            onClick={() => handleMethodSelect('EMAIL')}
            disabled={methodLoading}
          >
            <KTIcon iconName='sms' className='fs-3x text-primary me-5' />
            <div className='text-start'>
              <div className='fw-bolder fs-4 text-dark mb-1'>Email OTP</div>
              <div className='text-gray-500 fw-semibold fs-6'>Receive a code via your registered email</div>
            </div>
          </button>

          <button
            type='button'
            className='btn btn-outline btn-outline-dashed btn-outline-primary btn-active-light-primary p-7 d-flex align-items-center'
            onClick={() => handleMethodSelect('AUTHENTICATOR')}
            disabled={methodLoading}
          >
            <KTIcon iconName='scan-barcode' className='fs-3x text-primary me-5' />
            <div className='text-start'>
              <div className='fw-bolder fs-4 text-dark mb-1'>Authenticator App</div>
              <div className='text-gray-500 fw-semibold fs-6'>Use Google Authenticator to generate a code</div>
            </div>
          </button>
        </div>

        {methodLoading && (
          <div className='text-center py-4'>
            <span className='spinner-border text-primary' role='status'></span>
            <div className='text-gray-500 mt-2'>Processing...</div>
          </div>
        )}

        <div className='d-grid'>
          {onCancel && (
            <button
              type='button'
              className='btn btn-light'
              onClick={onCancel}
              disabled={methodLoading}
            >
              Back to Login
            </button>
          )}
        </div>
      </div>
    )
  }

  // SCREEN 2: OTP Verification
  return (
    <div className='w-100 p-2'>
      {/* Header */}
      <div className='text-center mb-8'>
        <div className='d-inline-flex align-items-center justify-content-center w-60px h-60px rounded-circle bg-light-primary mb-4'>
          <KTIcon iconName={selectedMethod === 'EMAIL' ? 'sms' : 'shield-tick'} className='fs-2x text-primary' />
        </div>
        <h2 className='text-dark fw-bolder mb-2'>
          {selectedMethod === 'EMAIL' ? 'Email Verification' : 'Two-Factor Authentication'}
        </h2>
        <div className='text-gray-500 fw-semibold fs-6'>
          {selectedMethod === 'EMAIL' 
            ? 'We have sent a verification code to your email' 
            : 'Protect your account with Google Authenticator'}
        </div>
      </div>

      {/* Loading State */}
      {loading && (
        <div className='text-center py-10'>
          <div className='spinner-border text-primary me-2' role='status'>
            <span className='visually-hidden'>Loading...</span>
          </div>
          <div className='text-gray-600 fw-medium mt-3 fs-6'>
            Setting up security key...
          </div>
        </div>
      )}

      {/* Error State */}
      {!loading && error && (
        <div className='alert alert-dismissible bg-light-danger border border-danger border-dashed d-flex flex-column p-5 mb-8'>
          <div className='d-flex align-items-center mb-2'>
            <KTIcon iconName='cross-circle' className='fs-2hx text-danger me-4' />
            <div className='d-flex flex-column'>
              <h5 className='mb-1 text-danger'>Setup Failed</h5>
              <span className='fs-7 text-gray-700'>{error}</span>
            </div>
          </div>
          <div className='d-flex justify-content-end gap-2 mt-3'>

            <button
              type='button'
              className='btn btn-sm btn-danger'
              onClick={() => window.location.reload()}
            >
              Retry
            </button>
          </div>
        </div>
      )}

      {/* Main 2FA Setup & Verification Form */}
      {!loading && !error && (
        <form onSubmit={handleVerifySubmit} className='form w-100'>
          {/* Step 1: Scan QR Code (Only shown when setting up 2FA for the first time and AUTHENTICATOR is selected) */}
          {selectedMethod === 'AUTHENTICATOR' && !isAlreadySetup && (qrCodeUrl || secretKey) && (
            <div className='bg-light-primary rounded border border-primary border-dashed p-5 mb-8 text-center'>
              <div className='fw-bold text-dark fs-6 mb-3'>
                1. Scan QR Code in Google Authenticator
              </div>

              {qrCodeUrl ? (
                <div className='d-flex justify-content-center my-3'>
                  <div className='p-3 bg-white rounded shadow-sm border'>
                    <img
                      src={qrCodeUrl}
                      alt='Google Authenticator 2FA QR Code'
                      style={{width: '180px', height: '180px', objectFit: 'contain'}}
                    />
                  </div>
                </div>
              ) : (
                <div className='py-4 text-muted fs-7'>
                  <KTIcon iconName='scan-barcode' className='fs-2x mb-2 text-primary' />
                  <br />
                  Use the manual secret key below to add 2FA to Google Authenticator.
                </div>
              )}

              {/* Secret Key Display with Copy Button */}
              {secretKey && (
                <div className='mt-4'>
                  <div className='text-gray-600 fs-7 mb-2 fw-semibold'>
                    Or enter secret key manually:
                  </div>
                  <div className='d-flex align-items-center justify-content-center gap-2'>
                    <code className='bg-white px-4 py-2 rounded border font-monospace text-dark fw-bold fs-6 tracking-wide select-all'>
                      {secretKey}
                    </code>
                    <button
                      type='button'
                      className={clsx(
                        'btn btn-sm',
                        copied ? 'btn-success' : 'btn-light-primary'
                      )}
                      onClick={handleCopySecretKey}
                      title='Copy Secret Key'
                    >
                      <KTIcon
                        iconName={copied ? 'check' : 'copy'}
                        className='fs-3 me-1'
                      />
                      {copied ? 'Copied!' : 'Copy'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* 6-Digit OTP Entry */}
          <div className='mb-8'>
            <label className='form-label fw-bold text-dark fs-6 text-center d-block mb-4'>
              {selectedMethod === 'EMAIL' 
                ? 'Enter 6-digit Code' 
                : isAlreadySetup
                  ? 'Enter 6-digit Authenticator Code'
                  : '2. Enter 6-digit Authenticator Code'}
            </label>

            <div className='d-flex justify-content-center gap-2 mb-3'>
              {otp.map((digit, index) => (
                <input
                  key={index}
                  ref={(el) => (inputRefs.current[index] = el)}
                  type='text'
                  inputMode='numeric'
                  maxLength={1}
                  value={digit}
                  onChange={(e) => handleOtpChange(index, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(index, e)}
                  className='form-control form-control-solid text-center fw-bolder fs-2'
                  style={{
                    width: '46px',
                    height: '54px',
                    borderRadius: '8px',
                    border: '1px solid #e1e3ea',
                  }}
                  autoFocus={index === 0}
                />
              ))}
            </div>
            <div className='text-muted text-center fs-7'>
              {selectedMethod === 'EMAIL'
                ? 'Check your inbox or spam folder for the code.'
                : 'Open Google Authenticator app on your phone to get your 6-digit code.'}
            </div>
          </div>

          {/* Action Buttons */}
          <div className='d-grid gap-2 mb-4'>
            <button
              type='submit'
              className='btn btn-primary fw-bolder fs-6 py-3'
              disabled={verifying || otp.join('').length < 6}
            >
              {!verifying && (
                <span>
                  <KTIcon iconName='check-circle' className='fs-2 me-2' />
                  {selectedMethod === 'EMAIL' 
                    ? 'Verify Code'
                    : isAlreadySetup ? 'Verify & Log In' : 'Verify & Enable 2FA'}
                </span>
              )}
              {verifying && (
                <span className='indicator-progress' style={{display: 'block'}}>
                  Verifying...
                  <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                </span>
              )}
            </button>

            {selectedMethod === 'EMAIL' && (
              <button
                type='button'
                className='btn btn-light me-0'
                onClick={async () => {
                  setVerifying(true)
                  try {
                    await requestEmailOTP(tempToken)
                    Swal.fire({
                      icon: 'success',
                      title: 'OTP Sent',
                      text: 'A new code has been sent to your email.',
                      timer: 2000,
                      showConfirmButton: false,
                    })
                  } catch (e: any) {
                    Swal.fire({
                      icon: 'error',
                      title: 'Error',
                      text: e?.response?.data?.message || e?.message || 'Failed to resend OTP.',
                    })
                  } finally {
                    setVerifying(false)
                  }
                }}
                disabled={verifying}
              >
                Resend OTP
              </button>
            )}

            {selectedMethod === 'AUTHENTICATOR' && isAlreadySetup && (
              <button
                type='button'
                className='btn btn-light-primary fw-bolder fs-6 py-3'
                onClick={handleReset2FA}
                disabled={verifying || resetting}
              >
                {resetting ? (
                  <span className='indicator-progress' style={{display: 'block'}}>
                    Resetting...
                    <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                  </span>
                ) : (
                  <span>
                    <KTIcon iconName='arrows-circle' className='fs-2 me-2' />
                    Reset 2FA
                  </span>
                )}
              </button>
            )}

            {onCancel && (
              <button
                type='button'
                className='btn btn-link text-muted me-0 mt-2'
                onClick={onCancel}
                disabled={verifying}
              >
                Back to Login
              </button>
            )}
          </div>
        </form>
      )}
    </div>
  )
}
